import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getItems, react } from '@/client/sdk.gen';
import { getItemOptions, getItemQueryKey } from '@/client/@tanstack/react-query.gen';
import type { GetItemsData, GetItemsResponse, GetItemResponse } from '@/client/types.gen';
import { readCache } from '@/store/action';
import { useStore } from '@/store';

// Export API types directly for use across the app
export type ApiItem = GetItemsResponse['data'][number];
export type ApiItemDetail = GetItemResponse;

// Item stats type from API
export type ItemStats = ApiItem['stats'];

// Reaction type from API
export type ReactionType = 0 | 1 | 2;

export type SortType = 'rank' | 'wishlist' | 'like7d' | 'like30d';

// Infinite scroll hook for items list - returns raw API data
export function useItemsInfinite(
  sort?: SortType,
  filter?: {
    q?: string;
    rare?: number[];
    genre?: number[];
    gender?: number[];
    category?: number[];
    tags?: number[];
    powerType?: 1;
    power?: string[];
    weapon?: number[];
    own?: 1;
  },
  limit: number = 20,
  enabled: boolean = true
) {
  const lang = useStore((s) => s.language);

  return useInfiniteQuery({
    queryKey: ['items', 'infinite', sort, filter, lang],
    enabled,
    queryFn: async ({ pageParam }) => {
      const { data } = await getItems({
        body: {
          cursor: pageParam,
          limit,
          filter: filter as GetItemsData['body']['filter'],
          sort,
        },
        query: { lang },
        throwOnError: true,
      });
      return data;
    },
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) => lastPage?.cursor,
    select: (data) => ({
      pages: data.pages,
      pageParams: data.pageParams,
      items: Array.from(
        new Map(data.pages.flatMap(page => page?.data || []).map(item => [item.id, item])).values()
      ),
    }),
  });
}

// Single item detail query hook - returns raw API data
export function useItemDetail(id: string | number | undefined) {
  const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
  const lang = useStore((s) => s.language);
  return useQuery({
    ...getItemOptions({ path: { id: numericId! }, query: { lang } }),
    enabled: !!numericId && !isNaN(numericId),
    // No transformation - return raw API data
  });
}

// Read item from cache
export function getItemFromCache(id: string | number): GetItemResponse | undefined {
  const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
  return readCache<ReturnType<typeof getItemQueryKey>, GetItemResponse>(
    getItemQueryKey({ path: { id: numericId } })
  );
}

// Mutation hook for reacting to items (like/dislike)
export function useItemReaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId, type }: { itemId: number; type: ReactionType }) => {
      const { data } = await react({
        path: { id: itemId },
        body: { type },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (data) => {
      if (!data) return;

      // 1. Update the item detail cache (only like, dislike, reaction)
      const detailQueryKey = getItemQueryKey({ path: { id: data.id } });
      queryClient.setQueryData<GetItemResponse>(detailQueryKey, (oldData) => {
        if (!oldData) return oldData;
        return { ...oldData, like: data.like, dislike: data.dislike, reaction: data.reaction };
      });

      // 2. Update the item in all infinite query caches
      queryClient.setQueriesData<{
        pages: GetItemsResponse[];
        pageParams: (number | undefined)[];
      }>(
        { queryKey: ['items', 'infinite'] },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map(page => ({
              ...page,
              data: page.data.map(item =>
                item.id === data.id
                  ? { ...item, like: data.like, dislike: data.dislike, reaction: data.reaction }
                  : item
              ),
            })),
          };
        }
      );
    },
  });
}
